import { useEffect, useRef } from 'react';
import moment from 'moment';
import { announce } from '../components/UI/LiveRegion';

/*
 * Keyboard navigation wrapper for `react-datetime` calendar & time popups.
 *
 * KBD-P14 (A11Y-14): the third-party picker has no built-in arrow-key nav in
 * its day grid or time counters. Rather than replace the library, we listen
 * for keydown on `document` (capture phase, so we run before Bootstrap /
 * react-datetime's own handlers) and scope events to any element inside an
 * open `.rdt.rdtOpen` wrapper. Open pickers are also given roles and names
 * (grid, header buttons, spin buttons) and month/view changes are announced.
 * Handles two popup shapes:
 *
 *   1. Calendar grid — days (7 columns), months and years (4 columns)
 *      - Arrow keys              — previous / next cell, row up / down
 *      - Home / End              — first / last cell of the visible grid
 *      - PageUp / PageDown       — previous / next month, year or decade
 *      - Enter / Space           — choose the focused cell (a month or year
 *                                  opens the next view down)
 *      - Escape                  — close the picker + refocus input
 *
 *   2. Time counters (`.rdtCounters` present)
 *      - Focus the currently-focused `.rdtCounter` (Tab to move between hour /
 *        minute / AM-PM)
 *      - ArrowUp / ArrowDown     — increment / decrement the focused counter
 *        (dispatched as `mousedown` since react-datetime wires the increment
 *        buttons via `onMouseDown`, not `onClick`)
 *      - Enter / Escape          — close the picker + refocus input
 *
 * The hook is a no-op until a picker opens. Attaches only one document
 * listener regardless of how many Datetime instances are on the page.
 */
const NAV_KEYS = new Set([
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End', 'PageUp', 'PageDown',
    'Enter', ' ', 'Escape',
]);

const CALENDAR_VIEWS = [
    { container: '.rdtDays', cell: '.rdtDay', columns: 7, previous: 'previous', next: 'next' },
    { container: '.rdtMonths', cell: '.rdtMonth', columns: 4, previous: 'previousYear', next: 'nextYear' },
    { container: '.rdtYears', cell: '.rdtYear', columns: 4, previous: 'previousYears', next: 'nextYears' },
];

let switchIdCounter = 0;
const lastHeaderText = new WeakMap();

const setAttr = (el, name, value) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
};

const getCalendarGrid = (picker) => {
    for (const view of CALENDAR_VIEWS) {
        const container = picker.querySelector(view.container);
        if (!container) continue;
        const cells = Array.from(container.querySelectorAll(`tbody ${view.cell}`));
        const header = container.querySelector('.rdtSwitch');
        return { view, cells, header, table: cells[0]?.closest('table') || null, headerTable: header?.closest('table') || null, columns: view.columns };
    }
    return null;
};

const isInMonth = (cell) => !cell.classList.contains('rdtOld') && !cell.classList.contains('rdtNew');

const getGridStop = (cells) => (
    cells.find((cell) => cell === document.activeElement)
    || cells.find((cell) => cell.classList.contains('rdtActive'))
    || cells.find((cell) => cell.classList.contains('rdtToday') && !cell.classList.contains('rdtDisabled'))
    || cells.find((cell) => isInMonth(cell) && !cell.classList.contains('rdtDisabled'))
    || cells[0]
);

const focusCell = (cells, cell) => {
    if (!cell) return;
    cells.forEach((c) => setAttr(c, 'tabindex', c === cell ? '0' : '-1'));
    cell.focus({ preventScroll: true });
};

const holdFocusInPicker = (openPicker) => {
    const box = openPicker.querySelector('.rdtPicker');
    if (!box) return;
    setAttr(box, 'tabindex', '-1');
    box.focus({ preventScroll: true });
};

const focusGridAfterRender = (picker, sameText) => {
    setTimeout(() => {
        const grid = getCalendarGrid(picker);
        if (!grid) return;
        const match = sameText && grid.cells.find((cell) => isInMonth(cell) && cell.textContent.trim() === sameText);
        focusCell(grid.cells, match || getGridStop(grid.cells));
    }, 0);
};

const decoratePicker = (wrapper, labels) => {
    const picker = wrapper.querySelector('.rdtPicker');
    if (!picker) return;
    picker.querySelectorAll('.rdtPrev, .rdtNext, .rdtSwitch').forEach((el) => {
        setAttr(el, 'role', 'button');
        setAttr(el, 'tabindex', '0');
    });
    picker.querySelectorAll('.rdtPrev > span, .rdtNext > span').forEach((el) => setAttr(el, 'aria-hidden', 'true'));
    const grid = getCalendarGrid(picker);
    if (grid && grid.table) {
        const previousLabel = labels[grid.view.previous];
        const nextLabel = labels[grid.view.next];
        if (previousLabel) picker.querySelectorAll('.rdtPrev').forEach((el) => setAttr(el, 'aria-label', previousLabel));
        if (nextLabel) picker.querySelectorAll('.rdtNext').forEach((el) => setAttr(el, 'aria-label', nextLabel));
        setAttr(grid.table, 'role', 'grid');
        if (grid.headerTable && grid.headerTable !== grid.table) setAttr(grid.headerTable, 'role', 'presentation');
        const { header } = grid;
        if (header) {
            if (!header.id) header.id = 'rdt-switch-' + (switchIdCounter += 1);
            setAttr(grid.table, 'aria-labelledby', header.id);
            const headerText = header.textContent.trim();
            const previousText = lastHeaderText.get(wrapper);
            if (previousText !== undefined && previousText !== headerText) announce(headerText);
            lastHeaderText.set(wrapper, headerText);
        }
        const stop = getGridStop(grid.cells);
        grid.cells.forEach((cell) => {
            setAttr(cell, 'tabindex', cell === stop ? '0' : '-1');
            setAttr(cell, 'aria-selected', cell.classList.contains('rdtActive') ? 'true' : 'false');
            if (cell.classList.contains('rdtDisabled')) setAttr(cell, 'aria-disabled', 'true');
            if (cell.classList.contains('rdtMonth') && header) {
                const monthLabel = moment([Number(header.textContent.trim()), Number(cell.getAttribute('data-value')), 1]).format('MMMM YYYY');
                setAttr(cell, 'aria-label', monthLabel);
            }
        });
    }

    picker.querySelectorAll('.rdtCounter').forEach((counter, index) => {
        const value = counter.querySelector('.rdtCount')?.textContent.trim() || '';
        setAttr(counter, 'role', 'spinbutton');
        setAttr(counter, 'tabindex', '0');
        if (labels.counters?.[index]) setAttr(counter, 'aria-label', labels.counters[index]);
        setAttr(counter, 'aria-valuetext', value);
        if (/^\d+$/.test(value)) setAttr(counter, 'aria-valuenow', String(Number(value)));
        counter.querySelectorAll('.rdtBtn').forEach((btn) => setAttr(btn, 'aria-hidden', 'true'));
    });
};

// react-datetime's time increment/decrement buttons fire via `onMouseDown`
// (starting internal state), then commit to the parent on `mouseup` fired at
// `document.body`. Dispatching both synchronously fails because React batches
// setState — the mouseup handler reads the pre-update state and commits the
// original value, so the change never lands. Defer the mouseup with
// `setTimeout(0)` so React flushes the mousedown state first (well under the
// library's 500ms continuous-increment threshold, so no extra ticks fire).
// The AM/PM counter is unaffected because `toggleDayPart` runs synchronously
// inside mousedown and doesn't depend on mouseup.
const pressTimeBtn = (btn) => {
    if (!btn) return;
    btn.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }));
    setTimeout(() => {
        btn.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, button: 0 }));
    }, 0);
};

// react-datetime closes through the `react-onclickoutside` HOC, which listens
// for `mousedown` and compares the event target against each instance's own
// DOM node. Dispatching the event *from* a given element therefore closes
// every picker that does not contain that element and leaves one that does
// alone — exactly the containment rule we want when focus moves.
const dismissPickersOutside = (target) => {
    const source = target instanceof Element ? target : document.body;
    source.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    // Pair it with a mouseup so the synthetic gesture is well formed.
    // react-bootstrap's Modal sets `waitingForMouseUp` on any mousedown inside
    // the dialog (to keep a drag that ends outside from dismissing it); an
    // unpaired mousedown would leave that latched until the user's next real
    // click. react-onclickoutside only listens for mousedown/touchstart, so
    // the extra event changes nothing about the dismissal itself.
    source.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
};

const closePicker = (openPicker) => {
    const input = openPicker.querySelector('input');
    // Dispatch from the body so every open picker is dismissed, then hand
    // focus back to the input the user came from (Escape / Enter semantics).
    dismissPickersOutside(document.body);
    if (input) input.focus({ preventScroll: true });
};

const handleCalendarGrid = (event, openPicker) => {
    const grid = getCalendarGrid(openPicker);
    if (!grid || grid.cells.length === 0) return false;
    const { cells, columns } = grid;
    if (event.target.tagName === 'INPUT' && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return false;

    const focusedIndex = cells.indexOf(document.activeElement);
    const index = focusedIndex !== -1 ? focusedIndex : cells.indexOf(getGridStop(cells));
    const move = (target) => {
        event.preventDefault(); event.stopPropagation();
        focusCell(cells, cells[Math.min(cells.length - 1, Math.max(0, target))]);
        return true;
    };

    switch (event.key) {
        case 'ArrowLeft':
            return move(index - 1);
        case 'ArrowRight':
            return move(index + 1);
        case 'ArrowUp':
            return move(focusedIndex === -1 ? index : index - columns);
        case 'ArrowDown':
            return move(focusedIndex === -1 ? index : index + columns);
        case 'Home':
            return move(0);
        case 'End':
            return move(cells.length - 1);
        case 'PageUp':
        case 'PageDown': {
            event.preventDefault(); event.stopPropagation();
            const sameText = focusedIndex !== -1 ? cells[focusedIndex].textContent.trim() : null;
            if (sameText) holdFocusInPicker(openPicker);
            openPicker.querySelector(event.key === 'PageUp' ? '.rdtPrev' : '.rdtNext')?.click();
            if (sameText) focusGridAfterRender(openPicker, sameText);
            return true;
        }
        case 'Enter':
        case ' ': {
            if (focusedIndex === -1) return false;
            event.preventDefault(); event.stopPropagation();
            const cell = cells[focusedIndex];
            const changesView = !cell.classList.contains('rdtDay');
            if (changesView) holdFocusInPicker(openPicker);
            cell.click();
            if (changesView) focusGridAfterRender(openPicker);
            return true;
        }
        case 'Escape':
            event.preventDefault(); event.stopPropagation();
            closePicker(openPicker);
            return true;
        default:
            return false;
    }
};

const handleTimeCounters = (event, openPicker) => {
    const countersBox = openPicker.querySelector('.rdtCounters');
    if (!countersBox) return false;
    const counters = Array.from(countersBox.querySelectorAll('.rdtCounter'));
    if (counters.length === 0) return false;

    // Make counters Tab-reachable so keyboard users can move between hour /
    // minute / AM-PM. Only need to do this once per open; idempotent.
    counters.forEach((c) => {
        if (!c.hasAttribute('tabindex')) c.setAttribute('tabindex', '0');
    });

    // Find the counter that currently holds focus (or is an ancestor of focus).
    let focusedCounter = counters.find((c) => c === document.activeElement || c.contains(document.activeElement));

    switch (event.key) {
        case 'ArrowUp': {
            if (!focusedCounter) {
                event.preventDefault(); event.stopPropagation();
                counters[0].focus({ preventScroll: true });
                return true;
            }
            event.preventDefault(); event.stopPropagation();
            // First `.rdtBtn` inside a counter is the "up" (increment) button.
            pressTimeBtn(focusedCounter.querySelector('.rdtBtn'));
            return true;
        }
        case 'ArrowDown': {
            if (!focusedCounter) {
                event.preventDefault(); event.stopPropagation();
                counters[0].focus({ preventScroll: true });
                return true;
            }
            event.preventDefault(); event.stopPropagation();
            // Second `.rdtBtn` is the "down" (decrement) button.
            const btns = focusedCounter.querySelectorAll('.rdtBtn');
            pressTimeBtn(btns[btns.length - 1]);
            return true;
        }
        case 'Enter':
        case 'Escape':
            event.preventDefault(); event.stopPropagation();
            closePicker(openPicker);
            return true;
        default:
            return false;
    }
};

const cycleFocusInPicker = (event, openPicker) => {
    const picker = openPicker.querySelector('.rdtPicker');
    if (!picker) return;
    const grid = getCalendarGrid(picker);
    const stops = grid && grid.cells.length
        ? [...picker.querySelectorAll('.rdtPrev, .rdtSwitch, .rdtNext'), getGridStop(grid.cells)]
        : Array.from(picker.querySelectorAll('.rdtCounter'));
    if (stops.length === 0) return;
    event.preventDefault(); event.stopPropagation();
    const active = document.activeElement;
    let index = stops.indexOf(active);
    if (index === -1 && grid?.cells.includes(active)) index = stops.length - 1;
    const next = stops[(index + (event.shiftKey ? -1 : 1) + stops.length) % stops.length];
    if (grid?.cells.includes(next)) focusCell(grid.cells, next);
    else next.focus({ preventScroll: true });
};

export const closeDatetimePickers = () => dismissPickersOutside(document.body);

const useDatetimeKeyboardNav = (labels = {}) => {
    const labelsRef = useRef(labels);
    labelsRef.current = labels;

    useEffect(() => {
        let pickerWithFocus = null;

        const handleKeyDown = (event) => {
            const openPicker = event.target?.closest?.('.rdt.rdtOpen');
            if (!openPicker) return;
            if (event.target.tagName !== 'INPUT' && !event.target.closest('.rdtPicker')) return;
            if (event.key === 'Tab' && event.target.closest('.rdtPicker')) {
                cycleFocusInPicker(event, openPicker);
                return;
            }
            if (!NAV_KEYS.has(event.key)) return;
            const navButton = event.target.closest('.rdtPrev, .rdtNext, .rdtSwitch');
            if (navButton && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault(); event.stopPropagation();
                const isHeader = navButton.classList.contains('rdtSwitch');
                if (isHeader) holdFocusInPicker(openPicker);
                navButton.click();
                if (isHeader) focusGridAfterRender(openPicker);
                return;
            }
            // Prefer the calendar grid; fall through to time counters if this
            // is a time-only picker.
            if (handleCalendarGrid(event, openPicker)) return;
            handleTimeCounters(event, openPicker);
        };

        // react-datetime only closes on an outside *mousedown*. Tabbing away,
        // or opening the timezone dropdown by keyboard, never produces one, so
        // the popup would otherwise stay open indefinitely while focus sits
        // somewhere else entirely. Close on focus leaving the wrapper instead.
        //
        // `focusin` rather than `blur`/`focusout`: focus legitimately moves
        // *into* the popup's own day cells and time counters (they carry a
        // roving tabindex and live inside `.rdt`), and a blur handler would
        // close the picker mid-interaction. The containment check below is
        // what makes that distinction.
        const handleFocusIn = (event) => {
            pickerWithFocus = event.target?.closest?.('.rdtPicker')?.closest('.rdt') || null;
            const openWrappers = Array.from(document.querySelectorAll('.rdt.rdtOpen'));
            if (openWrappers.length === 0) return;
            const target = event.target;
            if (!target || typeof target.contains !== 'function') return;
            const stillEngaged = openWrappers.some((wrapper) => (
                // Focus moved *into* the popup — day cells and time counters
                // carry a roving tabindex and live inside `.rdt`.
                wrapper.contains(target)
                // …or focus was merely *dropped upward* onto an ancestor. The
                // month arrows (`.rdtPrev` / `.rdtNext` are `<th>`) and the
                // time arrows (`.rdtBtn` is a `<span>`) are not focusable, so
                // clicking one blurs the input and the browser focuses the
                // nearest focusable ancestor instead — `.modal[tabindex="-1"]`
                // inside a Bootstrap modal, `<body>` outside one. That is the
                // user still working inside the picker, not leaving it.
                // A genuine outside click is already handled by react-datetime.
                || target.contains(wrapper)
            ));
            if (stillEngaged) return;
            // Dispatch from the newly focused element, not the body: if focus
            // moved to a *different* picker's input, that picker is mid-open
            // and must survive while the previous one closes.
            dismissPickersOutside(target);
        };

        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('focusin', handleFocusIn);

        let openWrappers = new Set();
        const decorateOpenPickers = () => {
            const nowOpen = new Set(document.querySelectorAll('.rdt.rdtOpen'));
            openWrappers.forEach((wrapper) => {
                if (nowOpen.has(wrapper) || wrapper !== pickerWithFocus) return;
                const active = document.activeElement;
                if (!active || active === document.body || wrapper.contains(active) || active.contains(wrapper)) {
                    pickerWithFocus = null;
                    wrapper.querySelector('input')?.focus({ preventScroll: true });
                }
            });
            openWrappers = nowOpen;
            nowOpen.forEach((wrapper) => decoratePicker(wrapper, labelsRef.current));
        };
        const pickerObserver = new MutationObserver(decorateOpenPickers);
        pickerObserver.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
        decorateOpenPickers();

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('focusin', handleFocusIn);
            pickerObserver.disconnect();
        };
    }, []);
};

export default useDatetimeKeyboardNav;
